import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../../TopNavBar.module.scss';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext';
import CurrencySelector from './components/CurrencySelector';
import { FeatureNotSupportedButton } from './components/FeatureNotSupportedButton';
import { FeatureNotActivated } from './components/FeatureNotActivatedButton';
import { ActiveCurrencyButton } from './components/ActiveCurrencyButton';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import { Currency } from 'shared/components/CurrencySelect/types';

export const CurrencyControl = ({ updateSessionCurrency, account }) => {

  const currencyContext = useCurrencyContext();
  const [currencyListOpened, setCurrencyListOpened] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>();
  const [featureIsNotSupported, setFeatureIsNotSupported] = useState(currencyContext.viewNotSupported);
  const [featureNotActive, setFeatureNotActive] = useState(!currencyContext.active);

  //eslint-disable-next-line no-undef
  const { currenciesArray, currenciesMap }: { currenciesArray: Currency[]; currenciesMap: Record<string, Currency> } =
    useMemo(() => {
      return { currenciesArray: currencyContext.currenciesArray, currenciesMap: currencyContext.currenciesMap };
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, currencyContext.currenciesArray, currencyContext.currenciesMap]);

  useEffect(() => {
    const featureFlagOn = currencyContext.active;
    const featureIsActiveButViewNotSupported = featureFlagOn && currencyContext.viewNotSupported;
    const featureIsActiveNoCurrencySession = featureFlagOn && !currencyContext.sessionCurrencyCode;
    const featureIsActiveWithCurrencySession = !!currencyContext.active && !!currencyContext.sessionCurrencyCode;
    let currency: Currency | null;

    switch (true) {
      case !featureFlagOn || featureIsActiveButViewNotSupported: {
        currency = currenciesMap[currencyContext.providerCurrency] ?? null;
        setFeatureIsNotSupported(true);
        setFeatureNotActive(!featureFlagOn);
        break;
      }
      case featureIsActiveNoCurrencySession: {
        currency = currenciesMap[currencyContext.processingCurrency ?? 'USD'] ?? null;
        setFeatureIsNotSupported(false);
        setFeatureNotActive(false);
        break;
      }
      case featureIsActiveWithCurrencySession: {
        currency = currenciesMap[currencyContext.sessionCurrencyCode!] ?? null;
        setFeatureIsNotSupported(false);
        setFeatureNotActive(false);
        break;
      }
      default: {
        currency = currenciesMap['USD'] ?? null;
        break;
      }
    }

    setSelectedCurrency(currency);
  }, [
    currencyContext.processingCurrency,
    currencyContext.sessionCurrencyCode,
    currencyContext.viewNotSupported,
    currencyContext.active,
    currencyContext.providerCurrency,
    currenciesMap,
  ]);

  useEffect(() => {
    setFeatureNotActive(!currencyContext.active);
    setSelectedCurrency(currencyContext.currencyInUse);
    if (currencyContext.active) {
      setFeatureIsNotSupported(currencyContext.viewNotSupported);
    }
  }, [
    currencyContext.processingCurrency,
    currencyContext.viewNotSupported,
    currencyContext.active,
    currencyContext.currencyInUse,
  ]);

  const toggleOpenList = useCallback(() => {
    setCurrencyListOpened(!currencyListOpened);
  }, [currencyListOpened]);

  const handleCurrencyClick = (currency: Currency) => {
    updateSessionCurrency(currency?.code);
    window.location.reload();
  };

  const resetCurrency = (event) => {
    event.preventDefault();
    updateSessionCurrency(null);
    const currency = currenciesMap[currencyContext.processingCurrency!] ?? null;
    setSelectedCurrency(currency);
    setCurrencyListOpened(false);
    window.location.reload();
  };

  if (currencyContext?.isLoading) {
    return (
      <div className={`${styles.roundButton} ${styles.currencyControl}`}>
        <div className={styles.currencyControlSpinner}>
          <Spinner size={SPINNER_SIZES.XL_SMALL} />
        </div>
      </div>
    );
  }

  
  if (featureNotActive) {
    return <FeatureNotActivated selectedCurrency={selectedCurrency!} />;
  }

  if (featureIsNotSupported) {
    return <FeatureNotSupportedButton selectedCurrency={selectedCurrency!} />;
  }

  return (
    <div className={styles.currencyButton}>
      <ActiveCurrencyButton
        toggleOpenList={toggleOpenList}
        selectedCurrency={selectedCurrency!}
        resetCurrency={resetCurrency}
      ></ActiveCurrencyButton>
      {currencyListOpened && (
        <div className={styles.modalParentClass}>
          <CurrencySelector
            onApply={handleCurrencyClick}
            currencyList={currenciesArray}
            onClose={() => setCurrencyListOpened(false)}
            selectedCurrency={selectedCurrency}
          />
        </div>
      )}
    </div>
  );
};
